import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || "",
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || "",
};

// Debug: Log if environment variables are missing
if (!poolData.UserPoolId || !poolData.ClientId) {
  console.error("⚠️  Missing Cognito environment variables!");
  console.error("UserPoolId:", poolData.UserPoolId || "MISSING");
  console.error("ClientId:", poolData.ClientId || "MISSING");
  console.error("Make sure .env file exists and restart the dev server!");
} else {
  console.log("✓ Cognito environment variables loaded successfully");
}

const userPool = new CognitoUserPool(poolData);
const pendingPasswordChallenges = new Map();

export const signIn = (email, password) => {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        pendingPasswordChallenges.delete(email.toLowerCase().trim());

        const idToken = result.getIdToken().getJwtToken();
        const accessToken = result.getAccessToken().getJwtToken();
        const refreshToken = result.getRefreshToken().getToken();

        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            reject(err);
            return;
          }

          const profile = {};
          attributes.forEach((attr) => {
            profile[attr.Name] = attr.Value;
          });

          const decodedToken = decodeJWT(idToken);

          const userProfile = {
            ...profile,
            ...decodedToken,
            sub: decodedToken.sub,
            email: decodedToken.email || profile.email,
            "cognito:groups": decodedToken["cognito:groups"] || [],
          };

          resolve({
            id_token: idToken,
            access_token: accessToken,
            refresh_token: refreshToken,
            profile: userProfile,
            expires_at: decodedToken.exp * 1000,
          });
        });
      },
      onFailure: (err) => {
        pendingPasswordChallenges.delete(email.toLowerCase().trim());
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        let challengeSession = null;

        if (cognitoUser.challengeParam && cognitoUser.challengeParam.Session) {
          challengeSession = cognitoUser.challengeParam.Session;
        } else if (cognitoUser.Session) {
          challengeSession = cognitoUser.Session;
        } else if (cognitoUser.challengeResponses?.Session) {
          challengeSession = cognitoUser.challengeResponses.Session;
        }

        pendingPasswordChallenges.set(email.toLowerCase().trim(), {
          cognitoUser,
          userAttributes: userAttributes || {},
          requiredAttributes: requiredAttributes || [],
          challengeSession: challengeSession,
        });

        reject({
          code: "NEW_PASSWORD_REQUIRED",
          message: "New password required",
          userAttributes: userAttributes || {},
          requiredAttributes: requiredAttributes || [],
        });
      },
    });
  });
};

export const signOut = () => {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
  localStorage.removeItem("cognito_user");
  localStorage.removeItem("cognito_tokens");
};

export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error("No user found"));
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err || !session.isValid()) {
        reject(err || new Error("Invalid session"));
        return;
      }

      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      const refreshToken = session.getRefreshToken().getToken();

      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        const profile = {};
        attributes.forEach((attr) => {
          profile[attr.Name] = attr.Value;
        });

        const decodedToken = decodeJWT(idToken);

        const userProfile = {
          ...profile,
          ...decodedToken,
          sub: decodedToken.sub,
          email: decodedToken.email || profile.email,
          "cognito:groups": decodedToken["cognito:groups"] || [],
        };

        resolve({
          id_token: idToken,
          access_token: accessToken,
          refresh_token: refreshToken,
          profile: userProfile,
          expires_at: decodedToken.exp * 1000,
        });
      });
    });
  });
};

export const refreshToken = () => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error("No user found"));
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      cognitoUser.refreshSession(session.getRefreshToken(), (err, session) => {
        if (err) {
          reject(err);
          return;
        }

        const idToken = session.getIdToken().getJwtToken();
        const accessToken = session.getAccessToken().getJwtToken();
        const newRefreshToken = session.getRefreshToken().getToken();

        const decodedToken = decodeJWT(idToken);

        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            reject(err);
            return;
          }

          const profile = {};
          attributes.forEach((attr) => {
            profile[attr.Name] = attr.Value;
          });

          const userProfile = {
            ...profile,
            ...decodedToken,
            sub: decodedToken.sub,
            email: decodedToken.email || profile.email,
            "cognito:groups": decodedToken["cognito:groups"] || [],
          };

          resolve({
            id_token: idToken,
            access_token: accessToken,
            refresh_token: newRefreshToken,
            profile: userProfile,
            expires_at: decodedToken.exp * 1000,
          });
        });
      });
    });
  });
};

const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Error decoding JWT:", e);
    return {};
  }
};

export const completeNewPassword = (
  email,
  newPassword,
  userAttributes = {}
) => {
  return new Promise((resolve, reject) => {
    const emailKey = email.toLowerCase().trim();
    const challengeData = pendingPasswordChallenges.get(emailKey);

    if (!challengeData || !challengeData.cognitoUser) {
      reject(
        new Error("No pending password challenge found. Please sign in again.")
      );
      return;
    }

    const cognitoUser = challengeData.cognitoUser;
    const storedUserAttributes = challengeData.userAttributes || {};
    const storedChallengeSession = challengeData.challengeSession;

    if (typeof newPassword !== "string") {
      reject(new Error("Password must be a string"));
      return;
    }

    const passwordString = newPassword.trim();
    if (!passwordString) {
      reject(new Error("Password cannot be empty"));
      return;
    }

    const mergedAttributes = { ...storedUserAttributes, ...userAttributes };

    let nameValue =
      mergedAttributes.name ||
      mergedAttributes.given_name ||
      mergedAttributes["custom:name"] ||
      "";

    if (!nameValue) {
      const emailUsername = email.split("@")[0];
      nameValue =
        emailUsername
          .split(".")
          .map(
            (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
          )
          .join(" ") ||
        emailUsername ||
        "User";
    }

    const username = cognitoUser.getUsername();
    let challengeSession = storedChallengeSession;

    if (!challengeSession) {
      if (cognitoUser.challengeParam?.Session) {
        challengeSession = cognitoUser.challengeParam.Session;
      } else if (cognitoUser.Session) {
        challengeSession = cognitoUser.Session;
      } else if (cognitoUser.challengeResponses?.Session) {
        challengeSession = cognitoUser.challengeResponses.Session;
      }
    }

    if (!challengeSession) {
      try {
        const attributeList = [
          new CognitoUserAttribute({
            Name: "name",
            Value: String(nameValue),
          }),
        ];

        cognitoUser.completeNewPasswordChallenge(
          passwordString,
          attributeList,
          {
            onSuccess: (result) => {
              pendingPasswordChallenges.delete(emailKey);
              const idToken = result.getIdToken().getJwtToken();
              const accessToken = result.getAccessToken().getJwtToken();
              const refreshToken = result.getRefreshToken().getToken();
              const decodedToken = decodeJWT(idToken);
              const userProfile = {
                ...decodedToken,
                sub: decodedToken.sub,
                email: decodedToken.email || email,
                name: nameValue,
                "cognito:groups": decodedToken["cognito:groups"] || [],
              };
              resolve({
                id_token: idToken,
                access_token: accessToken,
                refresh_token: refreshToken,
                profile: userProfile,
                expires_at: decodedToken.exp * 1000,
              });
            },
            onFailure: (err) => {
              pendingPasswordChallenges.delete(emailKey);
              reject(err);
            },
          }
        );
        return;
      } catch (fallbackError) {
        reject(
          new Error(
            "Could not retrieve challenge session. Please sign in again."
          )
        );
        return;
      }
    }

    const requestBody = {
      ChallengeName: "NEW_PASSWORD_REQUIRED",
      ClientId: poolData.ClientId,
      ChallengeResponses: {
        USERNAME: username,
        NEW_PASSWORD: passwordString,
        "userAttributes.name": String(nameValue),
      },
      Session: challengeSession,
    };

    const region = poolData.UserPoolId.split("_")[0];
    fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target":
          "AWSCognitoIdentityProviderService.RespondToAuthChallenge",
      },
      body: JSON.stringify(requestBody),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(
            data.message ||
              data.__type ||
              "Failed to complete password challenge"
          );
        }
        return data;
      })
      .then((data) => {
        if (data.AuthenticationResult) {
          const idToken = data.AuthenticationResult.IdToken;
          const accessToken = data.AuthenticationResult.AccessToken;
          const refreshToken = data.AuthenticationResult.RefreshToken;
          const decodedToken = decodeJWT(idToken);

          pendingPasswordChallenges.delete(emailKey);

          const userProfile = {
            ...decodedToken,
            sub: decodedToken.sub,
            email: decodedToken.email || email,
            name: nameValue,
            "cognito:groups": decodedToken["cognito:groups"] || [],
          };

          resolve({
            id_token: idToken,
            access_token: accessToken,
            refresh_token: refreshToken,
            profile: userProfile,
            expires_at: decodedToken.exp * 1000,
          });
        } else {
          reject(new Error("Unexpected response from Cognito"));
        }
      })
      .catch((error) => {
        pendingPasswordChallenges.delete(emailKey);
        reject(error);
      });
  });
};

/**
 * Refresh tokens using a refresh token (REST API).
 * Use when the SDK has no stored session (e.g. after completeNewPassword via fetch).
 *
 * @param {string} refreshToken - The refresh token
 * @returns {Promise<Object>} User object with id_token, access_token, refresh_token, profile, expires_at
 */
export const refreshTokenWithRefreshToken = (refreshToken) => {
  if (!refreshToken) {
    return Promise.reject(new Error("No refresh token provided"));
  }

  const region = poolData.UserPoolId.split("_")[0];
  const url = `https://cognito-idp.${region}.amazonaws.com/`;

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
    },
    body: JSON.stringify({
      AuthFlow: "REFRESH_TOKEN_AUTH",
      ClientId: poolData.ClientId,
      AuthParameters: {
        REFRESH_TOKEN: refreshToken,
      },
    }),
  })
    .then(async (response) => {
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.message || data.__type || "Failed to refresh token"
        );
      }
      return data;
    })
    .then((data) => {
      const result = data.AuthenticationResult;
      if (!result || !result.IdToken) {
        throw new Error("Unexpected response from Cognito");
      }

      const idToken = result.IdToken;
      const accessToken = result.AccessToken || "";
      const newRefreshToken = result.RefreshToken || refreshToken;
      const decodedToken = decodeJWT(idToken);

      const userProfile = {
        ...decodedToken,
        sub: decodedToken.sub,
        email: decodedToken.email || decodedToken["cognito:username"] || "",
        "cognito:groups": decodedToken["cognito:groups"] || [],
      };

      return {
        id_token: idToken,
        access_token: accessToken,
        refresh_token: newRefreshToken,
        profile: userProfile,
        expires_at: decodedToken.exp * 1000,
      };
    });
};

export const isAuthenticated = () => {
  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) {
    return false;
  }

  return new Promise((resolve) => {
    cognitoUser.getSession((err, session) => {
      if (err || !session.isValid()) {
        resolve(false);
      } else {
        resolve(true);
      }
    });
  });
};
